import {
  CURRENCY_POSITION,
  PAYMENT_CONTENT_LAYOUT,
  SEPARATOR_TYPE,
} from '@private/payment';

import { TObject } from 'utils/types';

import { FALLBACK_SEPARATOR_NAMES } from './constants';
import {
  IActiveTheme,
  IGoogleFont,
  IMerchant,
  IPaletteColor,
  ISeparators,
  ITheme,
  TLayoutsKeys,
  TPermanentPalette,
  TSeparatorName,
} from './types';

const SEPARATOR_NAMES = Object.keys(SEPARATOR_TYPE) as TSeparatorName[];

const toSeparatorName = (raw: unknown): TSeparatorName | undefined => {
  if (SEPARATOR_NAMES.includes(raw as TSeparatorName)) {
    return raw as TSeparatorName;
  }
  // TODO(CC-512): drop the legacy char → name mapping once the themes migration has run everywhere
  return SEPARATOR_NAMES.find((name) => SEPARATOR_TYPE[name] === raw);
};

export const normalizeSeparators = (raw: TObject | undefined): ISeparators => ({
  decimalSeparator:
    toSeparatorName(raw?.decimalSeparator) ?? FALLBACK_SEPARATOR_NAMES.decimalSeparator,
  thousandsSeparator:
    toSeparatorName(raw?.thousandsSeparator) ??
    FALLBACK_SEPARATOR_NAMES.thousandsSeparator,
});

export const normalizePalette = (data: TObject): IPaletteColor => {
  return {
    hue: (data?.hue as number) || 0,
    name: (data?.name as string) || '',
    saturation: (data?.saturation as number) || 0,
  };
};

export type IThemeData = TObject & {
  palette?: string | TObject[];
  permanentPalette?: string | TObject;
};

export type IThemePayloadData = TObject & {
  inputSeparators: TObject;
  textSeparators: TObject;
};

export const normalizeTheme = (theme: IThemePayloadData): ITheme => {
  let data: IThemeData;

  try {
    data = JSON.parse((theme?.theme as string) || '');
  } catch {
    data = {} as IThemeData;
  }
  return {
    amplitudeApiKey: (theme?.amplitudeApiKey as string) || null,
    clientMinAge: (theme?.clientMinAge as null | number) ?? null,
    createdAt: (theme?.createdAt as string) || '',
    currencyPosition: (theme?.currencyPosition ||
      CURRENCY_POSITION.END) as CURRENCY_POSITION,
    hideLogo: !!theme?.hideLogo,
    hideShopTitle: !!theme?.hideShopTitle,
    id: (theme?.id as string) || '',
    inputSeparators: normalizeSeparators(theme?.inputSeparators),
    isCompactMode: !!theme?.isCompactMode,
    isFullIframeMode: !!theme?.isFullIframeMode,
    isPciDssLogo: !!(theme?.isPciDssLogo as boolean),
    isSystem: theme?.merchantTerminalId === 'system',
    layoutName: (data?.layoutName as TLayoutsKeys) || '',
    microsoftClarityId: (theme?.microsoftClarityId as string) || null,
    name: (theme.name as string) || '',
    palette:
      typeof data?.palette === 'string'
        ? data?.palette
        : data?.palette?.map(normalizePalette) || [],
    paymentDetailsLogo: theme?.paymentDetailsLogo as string,
    paymentInfoLayout:
      (theme?.paymentInfoLayout as PAYMENT_CONTENT_LAYOUT) || PAYMENT_CONTENT_LAYOUT.BASE,
    permanentPalette:
      typeof data?.permanentPalette === 'string'
        ? data?.permanentPalette
        : (data?.permanentPalette as TPermanentPalette) || {},
    preview: (theme?.preview as string) || '',
    textSeparators: normalizeSeparators(theme?.textSeparators),
    theme: (data?.theme as TObject) || {},
    updatedAt: (theme?.updatedAt as string) || '',
  };
};

type TMerchantData = TObject & {
  themes?: IThemePayloadData[];
};

export const getSortedThemes = (themes: ITheme[], activeTheme: string): ITheme[] => {
  const sortThemes = (themes: ITheme[]) =>
    themes.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  const merchantActiveTheme: ITheme[] = [];
  const defaultThemes: ITheme[] = [];
  const merchantThemes: ITheme[] = [];

  themes.forEach((theme) => {
    if (theme.id === activeTheme) {
      merchantActiveTheme.push(theme);
    } else if (theme.isSystem) {
      defaultThemes.push(theme);
    } else {
      merchantThemes.push(theme);
    }
  });

  return merchantActiveTheme.concat(
    sortThemes(defaultThemes),
    sortThemes(merchantThemes),
  );
};

export function normalizeActiveTheme(data: TObject): IActiveTheme {
  return {
    activeTheme: (data?.activeTheme as string) || '',
  };
}

export function normalizeGoogleFont(key: string, data: TObject): IGoogleFont {
  return {
    family: (data?.family as string) || '',
    value: key,
    variants: data.weights as string[],
  };
}

export function normalizeMerchant(data: TMerchantData): IMerchant {
  return {
    activeTheme: (data?.activeTheme as string) || '',
    themes: data?.themes?.map(normalizeTheme) || [],
  };
}
